import {
  Autocomplete,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import { useSearchCustomersQuery } from '../../../lib/graphql';

import { candidateLabel } from './merge-wizard-types';

export interface SelectOption {
  _id: string;
  label: string;
}

export interface MergeWizardSelectStepProps {
  vendorId: string | undefined;
  // The originating customer — pre-selected and not removable.
  initiatingId: string;
  value: SelectOption[];
  onChange: (next: SelectOption[]) => void;
}

const MAX_RECORDS = 3;
// Minimum characters before we hit the API. Searching with an empty term makes
// the resolver return every customer for the vendor, which is unbounded for
// large vendors — so we only query once the user has typed a real prefix.
const MIN_SEARCH_LEN = 2;

export function MergeWizardSelectStep({
  vendorId,
  initiatingId,
  value,
  onChange,
}: MergeWizardSelectStepProps) {
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');

  const debouncedSetSearch = useMemo(
    () => debounce((v: string) => setSearch(v), 300),
    [],
  );
  useEffect(
    () => () => {
      debouncedSetSearch.cancel();
    },
    [debouncedSetSearch],
  );

  const trimmedSearch = search.trim();
  const hasSearch = trimmedSearch.length >= MIN_SEARCH_LEN;

  const { data, loading } = useSearchCustomersQuery({
    variables: { vendorId: vendorId as string, search: trimmedSearch },
    skip: !vendorId || !hasSearch,
    fetchPolicy: 'cache-and-network',
  });

  const options = useMemo<SelectOption[]>(() => {
    if (!hasSearch) return [];
    const rows = data?.searchCustomers ?? [];
    return rows.map((c) => ({ _id: c._id, label: candidateLabel(c) }));
  }, [data, hasSearch]);

  const atMax = value.length >= MAX_RECORDS;

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <Typography variant="body2">
        To merge duplicates, choose 2 or 3 customers. The customer you started
        from is included automatically.
      </Typography>

      <Autocomplete
        multiple
        options={options}
        value={value}
        onChange={(_, next) => {
          // Hiding the chip's delete icon doesn't stop Backspace or the clear
          // button removing the originating customer, so put it back if either
          // did: the merge is always "this customer and its duplicates".
          const initiating = value.find((o) => o._id === initiatingId);
          const kept =
            initiating && !next.some((o) => o._id === initiatingId)
              ? [initiating, ...next]
              : next;
          onChange(kept.slice(0, MAX_RECORDS));
        }}
        inputValue={input}
        onInputChange={(_, next) => {
          setInput(next);
          debouncedSetSearch(next);
        }}
        // Server-side search already filters; keep all returned rows as-is.
        filterOptions={(opts) => opts}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(a, b) => a._id === b._id}
        getOptionDisabled={() => atMax}
        loading={loading}
        noOptionsText={
          hasSearch ? 'No matching customers' : 'Type to search customers'
        }
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option._id}
              label={option.label}
              // The originating customer cannot be removed.
              onDelete={
                option._id === initiatingId
                  ? undefined
                  : getTagProps({ index }).onDelete
              }
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Add customers to merge"
            helperText={`Select 2–${MAX_RECORDS} customers.`}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Stack>
  );
}
